import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { CustomButton } from "@/components/Button";
import { StyledModal } from "@/components/StyledModal";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  body: { padding: 24, textAlign: "center", [theme.breakpoints.up("md")]: { padding: 32 } },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 20 } },
  description: { color: theme.palette.text.secondary, fontSize: 14, lineHeight: 1.5, marginTop: 8, marginBottom: 16 },
  buttons: { display: "flex", flexDirection: "column", gap: 12 },
}));

const useScrollLock = () => {
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
};

export const DraftPropertyModal: React.FC<{
  onClose: () => void;
  onClickSave: () => Promise<void>;
  onClickCancel: () => void;
}> = ({ onClose, onClickSave, onClickCancel }) => {
  const classes = useStyles();
  useScrollLock();
  return (
    <StyledModal role="dialog" ariaLabel="入力を保存して戻りますか" onClickOutside={onClose}>
      <Box className={classes.body}>
        <Typography component="h2" className={classes.title}>
          入力を保存して戻りますか
        </Typography>
        <Typography className={classes.description}>
          ここまでの入力を保存しておくと、次に開いたときに続きから入力できます。前回の下書きは上書きされます。
        </Typography>
        <Box className={classes.buttons}>
          <CustomButton
            type="button"
            buttonSize="L"
            onClick={() => {
              onClickSave();
              onClose();
            }}
          >
            保存して戻る
          </CustomButton>
          <CustomButton
            type="button"
            tone="outline"
            buttonSize="L"
            onClick={() => {
              onClickCancel();
              onClose();
            }}
          >
            保存せずに戻る
          </CustomButton>
          <CustomButton type="button" tone="text" buttonSize="L" onClick={onClose}>
            入力を続ける
          </CustomButton>
        </Box>
      </Box>
    </StyledModal>
  );
};

export const DraftPropertyReuseModal: React.FC<{
  onClose: () => void;
  onClickUse: () => Promise<void>;
  onClickDelete: () => void;
  lastSavedDate: string;
}> = ({ onClose, onClickUse, onClickDelete, lastSavedDate }) => {
  const classes = useStyles();
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  useScrollLock();
  return (
    <StyledModal role="dialog" ariaLabel="前回の入力が残っています" onClickOutside={onClose}>
      <Box className={classes.body}>
        <Typography component="h2" className={classes.title}>
          前回の入力が残っています
        </Typography>
        <Typography className={classes.description}>
          {lastSavedDate}に保存した入力があります。続きから入力しますか。
        </Typography>
        <Box className={classes.buttons}>
          {confirmDelete ? (
            <>
              <CustomButton type="button" tone="outline" buttonSize="L" onClick={onClickDelete}>
                下書きを削除する
              </CustomButton>
              <CustomButton type="button" tone="text" buttonSize="L" onClick={onClose}>
                キャンセル
              </CustomButton>
            </>
          ) : (
            <>
              <CustomButton type="button" buttonSize="L" onClick={onClickUse}>
                続きから入力する
              </CustomButton>
              <CustomButton type="button" tone="outline" buttonSize="L" onClick={onClickDelete}>
                最初から入力する
              </CustomButton>
              <CustomButton type="button" tone="text" buttonSize="M" onClick={() => setConfirmDelete(true)}>
                下書きを削除
              </CustomButton>
            </>
          )}
        </Box>
      </Box>
    </StyledModal>
  );
};
